import { useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { Helmet } from "react-helmet-async";
import { guides } from "../../data/guides";
import "./guidePost.css";

export default function GuidePost() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const post = useMemo(() => guides.find((g) => g.slug === slug), [slug]);

  if (!post) {
    return (
      <div className="post-container">
        <p>Guide not found.</p>
        <button className="back-btn" onClick={() => navigate("/guides")}>
          Back to Guides
        </button>
      </div>
    );
  }

  const otherGuides = guides.filter((g) => g.slug !== post.slug);

  return (
    <motion.div
      className="post-layout"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
    >
      {/* ✅ SEO */}
      <Helmet>
        <title>{post.seoTitle || post.title}</title>
        <meta
          name="description"
          content={post.seoDescription || post.excerpt}
        />
        <meta property="og:title" content={post.seoTitle || post.title} />
        <meta
          property="og:description"
          content={post.seoDescription || post.excerpt}
        />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.cover} />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.seoTitle || post.title} />
        <meta
          name="twitter:description"
          content={post.seoDescription || post.excerpt}
        />
      </Helmet>

      {/* Main Content */}
      <article className="post-article">
        <div className="post-cover-wrap">
          <img src={post.cover} alt={post.title} className="post-cover" />
        </div>

        <div className="post-header">
          <h1 className="post-title">{post.title}</h1>
          <div className="post-tags">
            {post.tags?.map((t) => (
              <span key={t} className="tag">
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="post-body markdown-body">
          {/* ✅ Render HTML safely */}
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
            {post.body}
          </ReactMarkdown>
        </div>

        <div className="post-footer">
          <Link to="/guides" className="back-link">
            ← Back to Guides
          </Link>
        </div>
      </article>

      {/* ✅ Animated Sidebar */}
      <aside className="sidebar">
        <h3>Explore Other Guides</h3>
        <motion.ul
          className="sidebar-list"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { staggerChildren: 0.1 },
            },
          }}
        >
          {otherGuides.map((g) => (
            <motion.li
              key={g.slug}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{ scale: 1.03, y: -3 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
              <Link to={`/guides/${g.slug}`}>
                <img
                  src={g.cover}
                  alt={g.title}
                  className="sidebar-thumb"
                  loading="lazy"
                />
                <span>{g.title}</span>
              </Link>
            </motion.li>
          ))}
        </motion.ul>
      </aside>
    </motion.div>
  );
}
